import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { AdminApiKey } from '../lib/types';
import { daysUntil, formatDate, relativeTime } from '../lib/format';
import { Badge, Card, Empty, Field, Modal, Notice, PageHead } from '../components/ui';

interface Profile {
  email: string | null;
  upn: string | null;
  displayName: string | null;
  isAdmin: boolean;
}

/**
 * Personal settings. For administrators this is also where the keys of the
 * admin API live — they belong to the gateway as a whole, not to any single
 * application, so they have no place on an application page.
 */
export function Settings({ profile }: { profile: Profile }) {
  return (
    <>
      <PageHead title="Settings" description="Your account and, for administrators, the keys to the admin API." />

      <Card title="Account" tight>
        <table>
          <tbody>
            <tr>
              <td className="muted">Name</td>
              <td>{profile.displayName ?? '—'}</td>
            </tr>
            <tr>
              <td className="muted">E-mail</td>
              <td>{profile.email ?? profile.upn ?? '—'}</td>
            </tr>
            <tr>
              <td className="muted">Role</td>
              <td>{profile.isAdmin ? 'Administrator' : 'User'}</td>
            </tr>
          </tbody>
        </table>
      </Card>

      {profile.isAdmin ? <AdminKeys /> : null}
    </>
  );
}

function AdminKeys() {
  const keys = useAsync(() => api.get<AdminApiKey[]>('/api/admin/admin-keys'));
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ secret: string; label: string } | null>(null);

  async function revoke(id: string): Promise<void> {
    if (!window.confirm('Revoke this key? Anything using it loses access to the admin API immediately.')) {
      return;
    }
    await api.delete(`/api/admin/admin-keys/${id}`);
    keys.reload();
  }

  return (
    <>
      {created ? (
        <Notice tone="ok" title="Key created - only visible now">
          <span className="mono" style={{ wordBreak: 'break-all' }}>{created.secret}</span>
          <br />
          <button
            className="small"
            style={{ marginTop: 8 }}
            onClick={() => void navigator.clipboard.writeText(created.secret)}
          >
            Copy to clipboard
          </button>{' '}
          <button className="small" onClick={() => setCreated(null)}>
            Hide
          </button>
        </Notice>
      ) : null}

      <Card
        title="Admin API keys"
        hint="For automation that configures the gateway without a sign-in"
        actions={
          <button className="primary small" onClick={() => setCreating(true)}>
            Create key
          </button>
        }
        tight
      >
        {keys.error ? <Notice tone="danger">{keys.error}</Notice> : null}
        {keys.data?.length ? (
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Identifier</th>
                <th>Expires</th>
                <th>Last used</th>
                <th>Created by</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.data.map((key) => (
                <tr key={key.id}>
                  <td>{key.label}</td>
                  <td className="mono muted">{key.key_id}</td>
                  <td>
                    {formatDate(key.expires_at)}{' '}
                    {key.state === 'active' && daysUntil(key.expires_at) <= 30 ? (
                      <Badge tone="warn">in {daysUntil(key.expires_at)} days</Badge>
                    ) : null}
                  </td>
                  <td className="muted">{relativeTime(key.last_used_at)}</td>
                  <td className="muted">{key.created_by ?? '—'}</td>
                  <td>
                    <Badge tone={key.state === 'active' ? 'ok' : ''}>{key.state}</Badge>
                  </td>
                  <td>
                    {key.state === 'active' ? (
                      <button className="small danger" onClick={() => void revoke(key.id)}>
                        Revoke
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : keys.loading ? (
          <Empty>Loading…</Empty>
        ) : (
          <Empty>No admin key created yet.</Empty>
        )}
      </Card>

      <Notice tone="info" title="What such a key may do">
        A key can do everything an administrator can — except create or revoke admin keys and grant
        administrator rights. Those stay with signed-in people, so that a leaked key cannot extend
        itself.
      </Notice>

      {creating ? (
        <CreateAdminKeyDialog
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setCreated(result);
            keys.reload();
          }}
        />
      ) : null}
    </>
  );
}

function CreateAdminKeyDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (result: { secret: string; label: string }) => void;
}) {
  const [label, setLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState(
    new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10),
  );
  const [ips, setIps] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    try {
      const result = await api.post<{ secret: string; label: string }>('/api/admin/admin-keys', {
        label,
        expiresAt: new Date(`${expiresAt}T23:59:59Z`).toISOString(),
        ipAllowlist: ips.split(/\s+/).map((value) => value.trim()).filter(Boolean),
      });
      onCreated(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <Modal
      title="Create admin API key"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void submit()} disabled={!label}>
            Create
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Label" hint="what is it used for">
        <input type="text" value={label} onChange={(event) => setLabel(event.target.value)} />
      </Field>
      <Field label="Expires on" hint="an expiry date is mandatory">
        <input type="date" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
      </Field>
      <Field label="Allowed IP addresses" hint="optional; one address or range per line">
        <textarea style={{ minHeight: 70 }} value={ips} onChange={(event) => setIps(event.target.value)} />
      </Field>
      <p className="muted tiny">
        The key is sent as <span className="mono">X-API-Key</span> and is valid only for{' '}
        <span className="mono">/api/admin</span>, never for the gateway. The secret is shown exactly
        once - afterwards it can only be replaced, not recovered.
      </p>
    </Modal>
  );
}
